import React, { useCallback, useEffect, useMemo } from 'react';
import debounce from 'lodash.debounce';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import VisibilitySensor from 'react-visibility-sensor';

import {
	postLRSSelfComplete,
	postLRSSlideView,
	postLRSVideoWatch,
} from '@techchange/base';
import {
	DangerAlert,
	IconButton,
	appPropTypes,
} from '@techchange/components';
import { messages } from '../../i18n';

import { RESOURCE_TYPES } from '../../constants';
import { isResourceOpen, formatResourceOpenMessage } from '../../utils';
import CompletionTracker from '../CompletionTracker';
import ResourceContentBuilder from '../ResourceContentBuilder';
import SelfCompleteButton from '../SelfCompleteButton';
import withResourceLogic from '../withResourceLogic';

import './ContentGridItem.scss';

const propTypes = {

	/**
	 * The resource.
	 */
	resource: PropTypes.shape({

		/**
		 * Unique ID for the resource
		 */
		id: PropTypes.string.isRequired,

		/**
		 * Title for the resource
		 */
		title: PropTypes.string.isRequired,

		/**
		 * Slug for the resource
		 */
		slug: PropTypes.string.isRequired,

		/**
		 * Type of the resource.
		 */
		type: PropTypes.oneOf([
			RESOURCE_TYPES.EMBED,
			RESOURCE_TYPES.FORM,
			RESOURCE_TYPES.IMAGE,
			// A link mark inside a slide is extracted into its own child
			// resource, so a link can show up alongside the other blocks
			RESOURCE_TYPES.LINK,
			RESOURCE_TYPES.POLL,
			RESOURCE_TYPES.MIRO,
			RESOURCE_TYPES.QUIZ,
			RESOURCE_TYPES.SLIDE,
			RESOURCE_TYPES.VIDEO,
		]).isRequired,

		/**
		 * The content of the resource.
		 */
		content: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),

		/**
		 * True, if the title should be visible for the resource.
		 */
		show_title: PropTypes.bool,

		/**
		 * The creator ID.
		 */
		creator_id: PropTypes.string,

		/**
		 * The resource scope.
		 */
		scope: PropTypes.string.isRequired,

		/**
		 * The resource scope ID.
		 */
		scope_id: PropTypes.string.isRequired,

		/**
		 * The organization ID.
		 */
		organization_id: PropTypes.string.isRequired,

		/**
		 * The children resources (if any).
		 */
		children: PropTypes.arrayOf(PropTypes.shape({

			/**
			 * The child resource ID.
			 */
			id: PropTypes.string,

		})),

	}).isRequired,

	/**
	 * The parent parent.
	 */
	parent: PropTypes.shape({

		/**
		 * Unique ID for the resource.
		 */
		id: PropTypes.string,

		/**
		 * Title for the resource.
		 */
		title: PropTypes.string,

		/**
		 * Type of the resource.
		 */
		type: PropTypes.oneOf([
			RESOURCE_TYPES.PAGE,
			RESOURCE_TYPES.MODULE,
			RESOURCE_TYPES.SUBMODULE,
			RESOURCE_TYPES.SUBMODULE_CONTENT
		]).isRequired,

		/**
		 * Creator id for the resource.
		 */
		creator_id: PropTypes.string,

	}).isRequired,

	/**
	 * True, if the user has admin privileges to create/edit/delete resources.
	 */
	isAdmin: PropTypes.bool.isRequired,

	/**
	 * The current user's ID (if logged-in).
	 */
	userId: PropTypes.string,

	/**
	 * The current user's timezone.
	 */
	userTimezone: PropTypes.string.isRequired,

	/**
	 * Admins Only: API integrations data for the current user.
	 */
	apiIntegrations: appPropTypes.apiIntegrations,

	/**
	 * The settings for filestack to handle image uploads and rendering.
	 */
	filestack: appPropTypes.filestack.isRequired,

	/**
	 * The users related to the scope of this content,
	 * (i.e. all enrolled users in a session).
	 *
	 * NOTE: Map from user ID -> user data.
	 */
	users: appPropTypes.allUsers,

	/**
	 * The TechChange socket instance to handle real-time events.
	 */
	socket: appPropTypes.socket,

	/**
	 * True, if the resource supports completion tracking (defaults to false).
	 */
	isUsingCompletionTracking: PropTypes.bool.isRequired,

	/**
	 * The associated form (if any) for the resource,
	 * injected by "withResourceLogic" HOC.
	 */
	form: PropTypes.shape({

		/**
		 * The form ID.
		 */
		id: PropTypes.string.isRequired,

	}),

	/**
	 * The associated rule/condition/subcondition/trackers (if any) for the resource,
	 * injected by "withResourceLogic" HOC.
	 */
	rule: PropTypes.shape({

		/**
		 * The rule ID.
		 */
		id: PropTypes.string,

		/**
		 * True, if the rule is active.
		 */
		active: PropTypes.bool,

		/**
		 * The rule condition.
		 */
		condition: PropTypes.shape({

			/**
			 * The condition ID.
			 */
			id: PropTypes.string.isRequired,

			/**
			 * True, if the condition is active.
			 */
			active: PropTypes.bool,

			/**
			 * The rule subconditions.
			 */
			subconditions: PropTypes.arrayOf(PropTypes.shape({

				/**
				 * The subcondition ID.
				 */
				id: PropTypes.string.isRequired,

				/**
				 * The subcondition completion tracker (if any) for the current user.
				 */
				tracker: PropTypes.shape({

					/**
					 * The completion tracker status.
					 */
					status: PropTypes.string.isRequired,

				}),

			})).isRequired,

			/**
			 * The condition completion tracker (if any) for the current user.
			 */
			tracker: PropTypes.shape({

				/**
				 * The completion tracker status.
				 */
				status: PropTypes.string.isRequired,

			}),

		}).isRequired,

		/**
		 * The rule completion tracker (if any) for the current user.
		 */
		tracker: PropTypes.shape({

			/**
			 * The completion tracker status.
			 */
			status: PropTypes.string.isRequired,

		}),

	}),

	/**
	 * The user's progress for the rule (as a percentage),
	 * injected by "withResourceLogic" HOC.
	 */
	progress: PropTypes.number,

	/**
	 * The user's completion tracker status for the rule,
	 * injected by "withResourceLogic" HOC.
	 */
	trackerStatus: PropTypes.string,

	/**
	 * The ID of the DOM node to use as the popover target for the resource toolbar,
	 * injected by "withResourceLogic" HOC.
	 */
	toolbarPopoverTargetId: PropTypes.string,

	/**
	 * The ID of the DOM node to use as the popover container for the resource toolbar,
	 * injected by "withResourceLogic" HOC.
	 */
	toolbarPopoverContainerId: PropTypes.string,

	/**
	 * The ID of the DOM node to use as the popover target for the completion requirements,
	 * injected by "withResourceLogic" HOC.
	 */
	completionRequirementsPopoverTargetId: PropTypes.string,

	/**
	 * True, if the current user is creating the resource,
	 * injected by "withResourceLogic" HOC.
	 */
	isCreating: PropTypes.bool.isRequired,

	/**
	 * True, if the current user is editing the resource,
	 * injected by "withResourceLogic" HOC.
	 */
	isEditing: PropTypes.bool.isRequired,

	/**
	 * The user error message to display, if any,
	 * injected by "withResourceLogic" HOC.
	 */
	errorMsg: PropTypes.string,

	/**
	 * Callback function to choose a drag source,
	 * injected by react-dnd.
	 */
	connectDragSource: PropTypes.func,

	/**
	 * Callback function to handle using an API integration.
	 */
	onUseApiIntegration: PropTypes.func,

	/**
	 * Callback function to handle cancelling the resource editor,
	 * injected by "withResourceLogic" HOC.
	 */
	onCancel: PropTypes.func.isRequired,

	/**
	 * Callback function to handle saving the resource editor,
	 * injected by "withResourceLogic" HOC.
	 */
	onSave: PropTypes.func.isRequired,

	/**
	 * Callback function to handle changing the language.
	 */
	onLanguageChange: PropTypes.func,

	/**
	 * Callback function to keep track of real-time changes to the
	 * content grid (i.e. resource width) while editing.
	 */
	onWidthChange: PropTypes.func,

	/**
	 * Callback function to handle the start of drag-and-drop.
	 */
	onDragStart: PropTypes.func,

	/**
	 * Callback function to handle the end of drag-and-drop.
	 */
	onDragEnd: PropTypes.func,

	/**
	 * Callback function to handle clicking a resource link.
	 */
	onClickResourceLink: PropTypes.func,

	/**
	 * Callback function to handle clicking on a user.
	 */
	onClickUser: PropTypes.func,

};

const defaultProps = {
	isAdmin: false,
	isUsingCompletionTracking: false,
};

const ContentGridItem = (props) => {

	const {
		resource,
		parent,
		isAdmin,
		userId,
		userTimezone,
		isUsingCompletionTracking,
		form,
		rule,
		progress,
		trackerStatus,
		toolbarPopoverTargetId,
		toolbarPopoverContainerId,
		completionRequirementsPopoverTargetId,
		isCreating,
		isEditing,
		errorMsg,
		users,
		apiIntegrations,
		filestack,
		socket,
		connectDragSource,
		onUseApiIntegration,
		onClickResourceLink,
		onClickUser,
		onCancel,
		onSave,
		onWidthChange,
		onLanguageChange,
		onDragStart,
		onDragEnd,
	} = props;

	// Avoid triggering duplicate "slide" -> "view" LRS statements
	// before 10 seconds have passed
	const debouncedLRSSlideView = debounce(
		() => postLRSSlideView(resource, parent),
		10000,
		{ leading: true },
	);

	const intl = useIntl();

	useEffect(() => {

		// Reset any changes to the width of the resource when it is done being edited
		if (isAdmin && !isEditing) {
			onWidthChange(resource.id, null);
		}

	}, [resource.id, isAdmin, isEditing, onWidthChange]);

	// Parse the resource to display
	const [resourceToRender, isReady] = useMemo(() => {

		const result = Object.assign({}, resource);

		let isResultReady = true;

		// Check if the form/poll/quiz resource has an associated form
		if (result.type === RESOURCE_TYPES.FORM ||
			result.type === RESOURCE_TYPES.POLL ||
			result.type === RESOURCE_TYPES.QUIZ) {

			if (!form && !isCreating) {
				isResultReady = false;
			}

			result.data = Object.assign({}, result.data, {
				form,
			});

		}

		return [result, isResultReady];

	}, [resource, form, isCreating]);

	const handleViewportChange = (isVisible) => {

		if (isVisible &&
			resource.type === RESOURCE_TYPES.SLIDE &&
			userId &&
			!isEditing &&
			!isCreating) {

			debouncedLRSSlideView();

		}

	};

	const handleVideoWatch = useCallback((meta) => {

		if (userId) {
			postLRSVideoWatch(resource, parent, meta);
		}

	}, [parent, resource, userId]);

	const handleLinkClick = useCallback((rEvent) => {

		if (onClickResourceLink) {
			onClickResourceLink(rEvent, resource, parent);
		}

	}, [onClickResourceLink, parent, resource]);

	const handleUserClick = useCallback((rEvent, userID) => {

		if (onClickUser) {
			onClickUser(rEvent, userID);
		}

	}, [onClickUser]);

	return (
		<VisibilitySensor
			partialVisibility="bottom"
			offset={{ bottom: -50 }}
			onChange={handleViewportChange}>
			<div
				id={resourceToRender.slug}
				className={`slide ${isEditing ? 'is-editing' : ''} ${isCreating ? 'is-creating' : ''}`}>
				<div className="slide-toolbar">
					{isAdmin && connectDragSource && connectDragSource(
						<div>
							<IconButton
								id={`tc-resource-${resourceToRender.id}-drag-source-btn`}
								className="resource-icon drag-source-handle"
								icon="fas fa-grip-vertical"
								size="24px"
								disabled={isEditing}
								aria-label={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_GRID_BUTTON_ARIA_LABEL_DRAG, {
									title: resource.title
								})}
								title={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_GRID_BUTTON_TITLE_DRAG)}
								isDraggable
								onMouseDown={onDragStart}
								onMouseUp={onDragEnd}
								onDragEnd={onDragEnd} />
						</div>
					)}
					{isAdmin && toolbarPopoverTargetId && toolbarPopoverContainerId &&
						<div
							id={toolbarPopoverContainerId}
							className="resource-settings-icon-container">
							<IconButton
								id={toolbarPopoverTargetId}
								className="resource-icon resource-settings-icon"
								icon="fas fa-gear"
								size="24px"
								disabled={isEditing}
								title={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_GRID_BUTTON_TITLE_VIEW_TOOLBAR)}
								aria-label={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_GRID_BUTTON_ARIA_LABEL_VIEW_TOOLBAR, {
									title: resource.title
								})} />
						</div>
					}
					{!isResourceOpen(resourceToRender) &&
						<IconButton
							id={`tc-resource-${resourceToRender.id}-open-date-btn`}
							className="resource-icon"
							icon="fas fa-lock"
							size="24px"
							title={formatResourceOpenMessage(resourceToRender, userTimezone, intl)}
							placement="top"
							tooltip />
					}
					{completionRequirementsPopoverTargetId &&
						<IconButton
							id={completionRequirementsPopoverTargetId}
							className={`req-resource-icon ${trackerStatus || ''}`}
							icon={
								<CompletionTracker
									progress={progress}
									status={trackerStatus} />
							}
							size="24px"
							title={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_GRID_BUTTON_ARIA_LABEL_VIEW_REQUIREMENTS, {
								title: resource.title
							})} />
					}
				</div>
				<div className="slide-content">
					{errorMsg &&
						<DangerAlert>
							{errorMsg}
						</DangerAlert>
					}
					{isReady &&
						<ResourceContentBuilder
							data={resourceToRender}
							isAdmin={isAdmin}
							isEditing={isEditing}
							userId={userId}
							users={users}
							apiIntegrations={apiIntegrations}
							filestack={filestack}
							socket={socket}
							triggers={{
								onLinkClick: handleLinkClick,
								onUserClick: handleUserClick,
								onVideoWatch: handleVideoWatch,
								onWidthChange,
								onLanguageChange,
							}}
							onUseApiIntegration={onUseApiIntegration}
							onCancel={onCancel}
							onSave={onSave} />
					}
					{isUsingCompletionTracking && !isEditing &&
						<SelfCompleteButton
							resource={resourceToRender}
							rule={rule}
							onClick={() => postLRSSelfComplete(resource, parent)} />
					}
				</div>
			</div>
		</VisibilitySensor>
	);

};

ContentGridItem.propTypes = propTypes;
ContentGridItem.defaultProps = defaultProps;

export default withResourceLogic(ContentGridItem);
